import * as React from "react";
import BriefCard from "common/components/briefCard";
import { useInView } from "react-intersection-observer";
import FeedHeader from "../feedHeader";
import { IconWrapper } from "common/icons";
import PACKS from "common/icons/packs";
import useDiscoverSlideHook from "app/discover/helpers/useDiscoverSlide";
import translateAppURLToURL from "@vingle/wtf-sdk/helpers/translateAppURLToURL";
import { VingleURL } from "@vingle/wtf-sdk/url";

const styles = require("./trendingContentSlide.scss");

interface IProps {
  categoryPosition: number;
  feedContent: Vingle.Discover.IDiscoverFeedContent;
}

const MOVE_SLIDE_SIZE = 3;

function TrendingContentItem({
  content,
  position,
  categoryPosition,
  inViewWrapper,
  wrapperRef,
}: {
  content: Vingle.Discover.IDiscoverContent;
  position: number;
  categoryPosition: number;
  inViewWrapper: boolean;
  wrapperRef: React.RefObject<HTMLDivElement>;
}) {
  const referralUrl = new VingleURL.DiscoverCategory({
    categoryIndex: categoryPosition,
  }).toSchema();
  return (
    <li className={styles.item}>
      <BriefCard
        title={content.title}
        content={content.preview.type === "card" ? content.preview.content : ""}
        authorName={content.desc.text}
        link={{
          pathname: translateAppURLToURL(content.url),
          state: {
            modal: true,
          },
        }}
        intersection={{
          root: wrapperRef.current,
          threshold: 0.5,
        }}
        trackTicket={
          inViewWrapper
            ? {
                contentType: "url",
                contentId: content.url,
                position,
                referralUrl,
              }
            : undefined
        }
      />
    </li>
  );
}

export default React.memo(function TrendingContentSlide(props: IProps) {
  const { feedContent, categoryPosition } = props;
  const {
    isDisabledPrev,
    isDisabledNext,
    handleMovePrev,
    handleMoveNext,
    slideStyle,
  } = useDiscoverSlideHook(feedContent.contents, MOVE_SLIDE_SIZE);
  const renderSlideButton = React.useCallback(
    (direction: "prev" | "next") => {
      if (feedContent.contents.length > MOVE_SLIDE_SIZE) {
        return (
          <button
            type="button"
            className={
              direction === "prev"
                ? styles.slidePrevButton
                : styles.slideNextButton
            }
            onClick={direction === "prev" ? handleMovePrev : handleMoveNext}
            disabled={direction === "prev" ? isDisabledPrev : isDisabledNext}
          >
            <IconWrapper
              icon={PACKS.TRENDING_DISCOVER.SUMMARY_CARD_ARROW}
              tagName="span"
            />
          </button>
        );
      } else {
        return null;
      }
    },
    [feedContent.contents.length, isDisabledPrev, isDisabledNext],
  );
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const [slideRef, inViewSlide] = useInView({
    triggerOnce: true,
  });
  return (
    <div className={styles.container}>
      <FeedHeader title={feedContent.title} />
      <div className={styles.slide} ref={slideRef}>
        {renderSlideButton("prev")}
        {renderSlideButton("next")}
        <div className={styles.wrapper} ref={wrapperRef}>
          <ul className={styles.list} style={slideStyle}>
            {feedContent.contents.map((content, index) => (
              <TrendingContentItem
                key={content.url}
                content={content}
                categoryPosition={categoryPosition}
                wrapperRef={wrapperRef}
                inViewWrapper={inViewSlide}
                position={index}
              />
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
});
